import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TerraceClosure {
  id: string;
  establishmentId: string;
  date: string;
  timeSlotId: string | null;
  reason: string | null;
  createdAt: string;
}

interface UseTerraceClosuresOptions {
  establishmentId?: string;
}

export const useTerraceClosures = (options: UseTerraceClosuresOptions = {}) => {
  const { establishmentId } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['terrace-closures', establishmentId],
    queryFn: async (): Promise<TerraceClosure[]> => {
      if (!establishmentId) return [];
      const { data, error } = await supabase
        .from('terrace_closures')
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('date', { ascending: true });
      if (error) throw error;
      return (data || []).map(row => ({
        id: row.id,
        establishmentId: row.establishment_id,
        date: row.date,
        timeSlotId: row.time_slot_id,
        reason: row.reason,
        createdAt: row.created_at,
      }));
    },
    enabled: !!establishmentId,
  });

  const addClosure = useMutation({
    mutationFn: async (params: { date: string; timeSlotId?: string | null; reason?: string }) => {
      if (!establishmentId) throw new Error('No establishment');
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('terrace_closures').insert({
        establishment_id: establishmentId,
        date: params.date,
        time_slot_id: params.timeSlotId || null,
        reason: params.reason || null,
        created_by: user?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terrace-closures', establishmentId] }),
  });

  const removeClosure = useMutation({
    mutationFn: async (closureId: string) => {
      const { error } = await supabase.from('terrace_closures').delete().eq('id', closureId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terrace-closures', establishmentId] }),
  });

  const removeClosuresByDate = useMutation({
    mutationFn: async (date: string) => {
      if (!establishmentId) throw new Error('No establishment');
      const { error } = await supabase.from('terrace_closures').delete()
        .eq('establishment_id', establishmentId)
        .eq('date', date);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terrace-closures', establishmentId] }),
  });

  // Helper: check if terrace is closed for a specific date (whole day or specific slot)
  const isTerraceClosedOnDate = (date: string, timeSlotId?: string): boolean => {
    const closures = query.data || [];
    // Whole day closure (time_slot_id IS NULL)
    if (closures.some(c => c.date === date && c.timeSlotId === null)) return true;
    // Specific slot closure
    if (timeSlotId && closures.some(c => c.date === date && c.timeSlotId === timeSlotId)) return true;
    return false;
  };

  const getClosuresForDate = (date: string): TerraceClosure[] => {
    return (query.data || []).filter(c => c.date === date);
  };

  return {
    closures: query.data || [],
    loading: query.isLoading,
    addClosure,
    removeClosure,
    removeClosuresByDate,
    isTerraceClosedOnDate,
    getClosuresForDate,
    refetch: query.refetch,
  };
};
